import { useMutation, useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import {
  Breadcrumb,
  Button,
  MenuButton,
  Modal,
  ResponsivePrimaryNav,
  SearchResultNavigation,
} from "../../../components/ui";
import { DELETE_WAREHOUSE_ITEM as deleteWarehouseItemMutation } from "../../../graphql/mutation/WarehouseItem";
import {
  FETCH_WAREHOUSE_ITEM as fetchWarehouseItem,
  FETCH_WAREHOUSE_ITEMS_LIST as fetchWarehouseItemsList,
} from "../../../graphql/query/WarehouseItem";
import useSearchResultNavigation from "../../../hooks/useSearchResultNavigation";
import useUser from "../../../hooks/useUser";
import { clearSelectionState } from "../../../util/clearSelectionState";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";
import routes from "../pages/Wizard/routes";
import AdhocReportBody from "./Actions/AdhocReportBody";
import exportModule from "./Actions/ExportModule";
import MoveItem from "./MoveItem";
import NewWarehouse from "./NewWarehouse";
import {
  warehouseFilterStore,
  warehouseNavigationStore,
} from "./useWarehouseFilter";
import WarehouseSearch from "./WarehouseSearch";
import {
  buildWarehouseNavigationTargetUrl,
  parseWarehouseItemId,
} from "../warehouseItemNavigation";

const fetchWarehouseNavigation = fetchWarehouseItemsList({});

const PrimaryNav = props => {
  const { formik } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const pathnames = location?.pathname.split("/");
  const search = warehouseFilterStore.get();

  const { hasPermission } = useUser();
  // get id from url
  const id = parseWarehouseItemId(pathnames?.length > 2 ? pathnames[2] : null);
  // fetch warehouse details if id is available
  // Only fetch minimal fields needed: id for breadcrumbs/delete, itemSite.siteId for MoveItem
  const warehouseObj = {
    itemSite: {
      siteId: true,
    },
  };
  const { data: { warehouseItem } = {} } = useQuery(
    fetchWarehouseItem(warehouseObj),
    {
      skip: !id,
      variables: { id },
    },
  );

  // find current submodule route
  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const listContext = useMemo(
    () => ({ locationSearch: location.search }),
    [location.search],
  );
  const {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  } = useSearchResultNavigation({
    activeSubPage: route?.href || "details",
    buildTargetUrl: buildWarehouseNavigationTargetUrl,
    dataKey: "warehouseItemsV2",
    fetchQuery: fetchWarehouseNavigation,
    listContext,
    navigate,
    navigationStore: warehouseNavigationStore,
    recordId: id,
    status,
  });
  const selected = Object.keys(formik?.values?.selected?.ids || {});
  const listId = +(queryParams.get("listId") || status === "selectListWorking"
    ? queryParams.get("workingListId")
    : null);
  const ids = selected?.map(key => {
    const obj = formik?.values?.selected?.ids?.[key];
    return obj?.item?.id || obj?.id;
  });
  const select = formik?.values?.selected?.select;
  const disabled = ids?.length === 0;

  const [
    deleteWarehouseItem,
    {
      data: {
        deleteWarehouseItem: { error: deleteWarehouseItemError } = {},
      } = {},
      loading: deleteWarehouseItemLoading,
      reset,
    },
  ] = useMutation(deleteWarehouseItemMutation());

  const statusMap = {
    all: "All Warehouse",
    searched: "Searched Warehouse",
    selectListWorking: "Select List (Working)",
  };
  const label = statusMap[status] || "All Warehouse";
  // prepare steps for breadcrumbs

  const steps = [
    {
      label,
      onClick: () =>
        navigate(`/warehouse${location.search || `?status=${status}`}`),
    },
    ...(warehouseItem
      ? [
          {
            label: warehouseItem?.id,
            onClick: () =>
              navigate(
                buildWarehouseNavigationTargetUrl({
                  listContext,
                  recordId: warehouseItem?.id,
                  status,
                }),
              ),
          },
          {
            label: route?.name,
            onClick: () =>
              navigate(
                buildWarehouseNavigationTargetUrl({
                  activeSubPage: route?.href,
                  listContext,
                  recordId: warehouseItem?.id,
                  status,
                }),
              ),
          },
        ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  const deleteModalProps = {
    title: deleteWarehouseItemError ? "Error" : "Delete Warehouse Item",
    scale: "sm",
    description: deleteWarehouseItemError
      ? deleteWarehouseItemError
      : "Are you sure you wish to delete this warehouse item? This cannot be undone.",
    closeOnBackdrop: true,
    body: ({ closeModal }) => {
      const handleDelete = () => {
        deleteWarehouseItem({
          variables: {
            input: { id: warehouseItem?.id },
          },
          update: (cache, { data }) => {
            const { deleteWarehouseItem: { success } = {} } = data || {};
            if (success) {
              closeModal();
              cache.evict({
                id: cache.identify({
                  __typename: "WarehouseItem",
                  id,
                }),
              });
              navigate("/warehouse");
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={deleteWarehouseItemError ? "Close" : "Cancel"}
            action={deleteWarehouseItemError ? "primary" : "default"}
            onClick={() => {
              reset();
              closeModal?.();
            }}
          />
          {!deleteWarehouseItemError && (
            <Button
              label={deleteWarehouseItemLoading ? "Deleting" : "Delete"}
              disabled={deleteWarehouseItemLoading}
              onClick={handleDelete}
            />
          )}
        </div>
      );
    },
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side state leaks into actions
  // here. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: id,
    selectedIds: ids,
    fallbacks: { listId, search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    onClose: () => {
      formik?.resetForm();
    },
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New Warehouse",
        modalProps: {
          body: NewWarehouse,
          closeOnBackdrop: false,
          hideCloseButton: true,
          scale: "md",
          isCreateOpen,
          onOpen: () => {
            setIsCreateOpen(true);
          },
          onClose: () => {
            setIsCreateOpen(false);
          },
        },
      },
      {
        disabled,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        disabled: !warehouseItem?.id && disabled,
        label: "Export Warehouse Report",
        modalProps: {
          ...defaultActionProps,
          body: ExportActiveReport,
          obj: {
            instance: {
              edition: {
                artwork: {
                  artists: {
                    name: true,
                  },
                },
              },
            },
          },
          optimise: true,
          type: "WarehouseItem",
          ids: warehouseItem?.id ? [warehouseItem?.id] : ids,
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      ...(warehouseItem?.id
        ? [
            {
              label: "Move Item",
              modalProps: {
                body: MoveItem,
                closeOnBackdrop: false,
                warehouseItem,
                hideCloseButton: true,
                scale: "md",
              },
            },
            {
              disabled: !hasPermission("DELETE_WAREHOUSE_ITEM"),
              label: "Delete warehouse item",
              modalProps: deleteModalProps,
            },
          ]
        : []),
    ],
  };

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const modalProps = {
    body: WarehouseSearch,
    closeOnBackdrop: true,
    scale: "fullscreen",
    isSearchOpen,
    onClick: () => setIsSearchOpen(false),
    onSearchSubmitted: () => clearSelectionState(formik),
  };

  return (
    <ResponsivePrimaryNav>
      <div>
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex items-center gap-2 sm:gap-4 lg:justify-end">
        <SearchResultNavigation
          hasNavigationContext={hasNavigationContext}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          navigationLoading={navigationLoading}
          navigateToNext={navigateToNext}
          navigateToPrevious={navigateToPrevious}
          showNavigationControls={showNavigationControls}
        />
        <Modal {...modalProps}>
          <Button
            label="Warehouse Search"
            action="black"
            onClick={() => setIsSearchOpen(true)}
          />
        </Modal>
        <MenuButton {...actionMenuProps} />
      </div>
    </ResponsivePrimaryNav>
  );
};

export default PrimaryNav;
